import {
  DashboardAlphaPlaceReportSummary,
  DashboardAlphaReportStatus,
  DashboardAlphaReportStatusEnum,
  DashboardAlphaReportSummaryCounts,
  DashboardAlphaScheduleOption,
  PostDashboardAlphaReportStatusesRequestBody,
  PostDashboardAlphaReportStatusesResponse,
  PostDashboardAlphaReportSummariesRequestBody,
  PostDashboardAlphaReportSummariesResponse,
} from '@ulysses-inc/harami_api_client'
import { getShowcaseReportUuids } from 'src/features/monitoringDashboardAlpha/demo/demoSettings'
import {
  DemoReportStatusRow,
  generateDemoReportStatuses,
} from 'src/features/monitoringDashboardAlpha/demo/generateDemoReportStatuses'
import { demoProfile } from 'src/features/monitoringDashboardAlpha/demo/profile'
import { toDateString } from 'src/util/datetime/toDateString'
import { ExhaustiveError } from 'src/util/error'
import { localeAwareSort } from 'src/util/sortStringsLocaleAware'

// 件数の省略時の既定値は harami_api_server（handler/dashboard_alpha.go）に揃える
const summariesDefaultLimit = 25
const statusesDefaultLimit = 50

type Options = {
  /** 現在時刻の取得元。テストで固定するために差し替えられるようにしている */
  getNow?: () => Date
  /** 実在レポートの UUID の取得元。テストで差し替えられるようにしている */
  getShowcaseReportUuids?: () => readonly string[]
}

const compareText = (a: string, b: string): number => {
  if (a < b) return -1
  return a > b ? 1 : 0
}

const buildEmptyCounts = (): DashboardAlphaReportSummaryCounts => ({
  implemented: 0,
  implementedAfterDeadline: 0,
  recording: 0,
  recordingOverdue: 0,
  notStarted: 0,
  duringPeriod: 0,
  notImplementedYet: 0,
  total: 0,
})

const addCount = (
  counts: DashboardAlphaReportSummaryCounts,
  status: DashboardAlphaReportStatusEnum,
) => {
  switch (status) {
    case DashboardAlphaReportStatusEnum.Implemented:
      counts.implemented += 1
      break
    case DashboardAlphaReportStatusEnum.ImplementedAfterDeadline:
      counts.implementedAfterDeadline += 1
      break
    case DashboardAlphaReportStatusEnum.Recording:
      counts.recording += 1
      break
    case DashboardAlphaReportStatusEnum.RecordingOverdue:
      counts.recordingOverdue += 1
      break
    case DashboardAlphaReportStatusEnum.NotStarted:
      counts.notStarted += 1
      break
    case DashboardAlphaReportStatusEnum.DuringPeriod:
      counts.duringPeriod += 1
      break
    case DashboardAlphaReportStatusEnum.NotImplementedYet:
      counts.notImplementedYet += 1
      break
    case DashboardAlphaReportStatusEnum.NotScheduled:
      // デモの行には出てこない。本物の API と同じく集計に含めない
      return
    default:
      throw new ExhaustiveError(status)
  }
  counts.total += 1
}

/**
 * 画面に出す実施率（0〜1）。selector/computeSubmissionRate と同じく開始前を除いた件数で割り、分母 0 は 0 とする。
 * L1 の並びに使う。実 API は total 分母で並べるが、画面の実施率（開始前を除く）と単調にならないので、デモでは表示に合わせる。
 */
const submissionRateOf = (
  counts: DashboardAlphaReportSummaryCounts,
): number => {
  const denominator = counts.total - counts.notStarted
  if (denominator === 0) return 0
  return (counts.implemented + counts.implementedAfterDeadline) / denominator
}

/**
 * placeNodeIds（現場・現場グループの UUID の混在）を、対象の現場 UUID の集合に展開する。
 * 現場グループは所属する現場へ展開し、知らない UUID は無視する。未指定（空配列を含む）は全現場。
 */
const expandPlaceNodeIds = (
  placeNodeIds: string[] | undefined,
): Set<string> | null => {
  if (!placeNodeIds || placeNodeIds.length === 0) return null
  const placeUuids = new Set<string>()
  for (const place of demoProfile.places) {
    if (
      placeNodeIds.includes(place.uuid) ||
      (place.placeGroupUuid !== null &&
        placeNodeIds.includes(place.placeGroupUuid))
    ) {
      placeUuids.add(place.uuid)
    }
  }
  return placeUuids
}

/**
 * offset / limit で 1 ページ分を切り出す。
 * 件数を超える開始位置は末尾のページに寄せる（サーバーの library.AdjustOffset と同じ）。
 */
const paginate = <T>(
  items: T[],
  offset: number | undefined,
  limit: number | undefined,
  defaultLimit: number,
): T[] => {
  const pageSize = limit !== undefined && limit > 0 ? limit : defaultLimit
  const requestedOffset = offset !== undefined && offset > 0 ? offset : 0
  const start =
    requestedOffset >= items.length
      ? Math.max(0, Math.ceil(items.length / pageSize) * pageSize - pageSize)
      : requestedOffset
  return items.slice(start, start + pageSize)
}

/**
 * 新実施状況（α版）の API（POST /dashboardAlpha/...）を、生成したダミーデータで置き換える。
 *
 * レスポンスの形・絞り込み・並び・ページングは harami_api_server の handler/dashboard_alpha.go に揃え、
 * 生成クライアントのレスポンス型で返す。呼び出し側の adaptResponse はそのまま通る。
 */
export const createDemoDashboardAlphaApi = (options: Options = {}) => {
  const getNow = options.getNow ?? (() => new Date())
  const resolveShowcaseReportUuids =
    options.getShowcaseReportUuids ?? getShowcaseReportUuids

  /** 期間内の全行を生成し、placeNodeIds で絞り込む */
  const loadRows = (
    startDate: Date,
    endDate: Date,
    placeNodeIds: string[] | undefined,
  ): DemoReportStatusRow[] => {
    const rows = generateDemoReportStatuses({
      // リクエストの日付はローカル日付の 0 時（stripTimeInfo 済み）なので、ローカル日付として読む
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      now: getNow(),
      showcaseReportUuids: resolveShowcaseReportUuids(),
    })
    const placeUuids = expandPlaceNodeIds(placeNodeIds)
    return placeUuids === null
      ? rows
      : rows.filter(row => placeUuids.has(row.placeNodeUUID))
  }

  const postDashboardAlphaReportSummaries = async (
    body: PostDashboardAlphaReportSummariesRequestBody,
  ): Promise<PostDashboardAlphaReportSummariesResponse> => {
    const rows = loadRows(body.startDate, body.endDate, body.placeNodeIds)

    // 行が 1 件もない現場は集計に現れない（本物の API と同じ）
    const summary = buildEmptyCounts()
    const placeSummaries = new Map<string, DashboardAlphaPlaceReportSummary>()
    for (const row of rows) {
      let placeSummary = placeSummaries.get(row.placeNodeUUID)
      if (!placeSummary) {
        placeSummary = {
          placeNodeUUID: row.placeNodeUUID,
          placeName: row.placeName,
          counts: buildEmptyCounts(),
        }
        placeSummaries.set(row.placeNodeUUID, placeSummary)
      }
      addCount(summary, row.status)
      addCount(placeSummary.counts, row.status)
    }

    // 実施率が低い現場を先頭にする。同率は現場名 → UUID で、ページをまたいでも並びが揺れないようにする
    const sorted = [...placeSummaries.values()]
      .map(placeSummary => ({
        placeSummary,
        rate: submissionRateOf(placeSummary.counts),
      }))
      .sort(
        (a, b) =>
          a.rate - b.rate ||
          localeAwareSort(a.placeSummary.placeName, b.placeSummary.placeName) ||
          compareText(
            a.placeSummary.placeNodeUUID,
            b.placeSummary.placeNodeUUID,
          ),
      )
      .map(({ placeSummary }) => placeSummary)

    return {
      summary,
      placeReportSummaries: paginate(
        sorted,
        body.offset,
        body.limit,
        summariesDefaultLimit,
      ),
      count: sorted.length,
    }
  }

  const postDashboardAlphaReportStatuses = async (
    body: PostDashboardAlphaReportStatusesRequestBody,
  ): Promise<PostDashboardAlphaReportStatusesResponse> => {
    let rows = loadRows(body.startDate, body.endDate, body.placeNodeIds)

    const statuses = body.statuses ?? []
    if (statuses.length > 0) {
      rows = rows.filter(row => statuses.includes(row.status))
    }

    // スケジュールの選択肢は、選択中のスケジュールで選択肢が消えないよう scheduleIds で絞る前の行から作る
    const scheduleNameByUuid = new Map<string, string>()
    for (const row of rows) {
      scheduleNameByUuid.set(row.scheduleUUID, row.scheduleName)
    }
    const scheduleOptions: DashboardAlphaScheduleOption[] = [
      ...scheduleNameByUuid,
    ]
      .map(([scheduleUUID, scheduleName]) => ({ scheduleUUID, scheduleName }))
      .sort(
        (a, b) =>
          localeAwareSort(a.scheduleName, b.scheduleName) ||
          compareText(a.scheduleUUID, b.scheduleUUID),
      )

    const scheduleIds = body.scheduleIds ?? []
    if (scheduleIds.length > 0) {
      rows = rows.filter(row => scheduleIds.includes(row.scheduleUUID))
    }

    // 日付 → スケジュール名 → 現場名 → 現場 UUID → スケジュール UUID の順にして、ページをまたいでも並びが揺れないようにする
    const sorted = [...rows].sort(
      (a, b) =>
        compareText(a.date, b.date) ||
        localeAwareSort(a.scheduleName, b.scheduleName) ||
        localeAwareSort(a.placeName, b.placeName) ||
        compareText(a.placeNodeUUID, b.placeNodeUUID) ||
        compareText(a.scheduleUUID, b.scheduleUUID),
    )

    return {
      reportStatuses: paginate(
        sorted,
        body.offset,
        body.limit,
        statusesDefaultLimit,
      ).map(
        // placeGroupUuid は内部用なので API の行の形に戻す
        ({ placeGroupUuid, ...row }): DashboardAlphaReportStatus => row,
      ),
      count: sorted.length,
      scheduleOptions,
    }
  }

  return { postDashboardAlphaReportSummaries, postDashboardAlphaReportStatuses }
}

/** フックから使う既定のインスタンス。現在時刻と、環境設定の実在レポート UUID を使う */
export const demoDashboardAlphaApi = createDemoDashboardAlphaApi()
